import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { listCommunicationLog } from '../../api/endpoints';
import { toApiPage, toApiSort, type GridPaginationModel } from '../../api/paging';
import { queryKeys } from '../../api/queryKeys';
import {
  communicationChannels,
  communicationLogSortKeys,
  communicationOutcomes,
  type CommunicationChannel,
  type CommunicationLogListItem,
  type CommunicationOutcome,
} from '../../api/types';
import { useIsAdministrator } from '../../auth/useCallerContext';
import { ErrorBlock, GridEmptyOverlay } from '../../components/QueryStateOverlay';
import { CommunicationChannelChip, CommunicationOutcomeChip } from '../../components/StatusChips';
import { communicationChannelLabelKeys, communicationOutcomeLabelKeys } from '../../components/statusLabels';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useNoTenantMessageKey, useSelectedTenant } from '../../tenant/tenantSelection';
import { formatDateTime } from '../../utils/format';

/**
 * Every exchange on the message channels, accepted or not. Operations show what the bridge took;
 * this is where a submission that never became one is explained — the refusals, the retries, the
 * acknowledgements sent back — searchable by whatever identifier the reader is holding.
 */
export function CommunicationLogPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdministrator = useIsAdministrator();
  const { selectedTenantGid } = useSelectedTenant();
  // The global selector is the one tenant control: a tenant user reads the tenant in force, an
  // administrator reads the selected one — or, with none selected, every tenant, which is the only
  // way the rows that resolved no tenant can be seen at all.
  const hasNoTenant = !isAdministrator && selectedTenantGid === null;
  const crossTenant = isAdministrator && selectedTenantGid === null;
  const noTenantKey = useNoTenantMessageKey();

  // ?search= pre-fills the box: the operation detail links here with its identifiers.
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const debouncedSearch = useDebouncedValue(search.trim());
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'recordedAt', sort: 'desc' }]);
  const [channels, setChannels] = useState<CommunicationChannel[]>([]);
  const [outcomes, setOutcomes] = useState<CommunicationOutcome[]>([]);
  const [recordedFrom, setRecordedFrom] = useState<Dayjs | null>(null);
  const [recordedTo, setRecordedTo] = useState<Dayjs | null>(null);

  const resetPage = () => setPaginationModel((current) => ({ ...current, page: 0 }));

  const filter = useMemo(
    () => ({
      page: toApiPage(paginationModel.page),
      pageSize: paginationModel.pageSize,
      ...toApiSort(sortModel, communicationLogSortKeys),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(channels.length > 0 ? { channels } : {}),
      ...(outcomes.length > 0 ? { outcomes } : {}),
      ...(recordedFrom?.isValid() ? { recordedFrom: recordedFrom.startOf('day').toISOString() } : {}),
      ...(recordedTo?.isValid() ? { recordedTo: recordedTo.endOf('day').toISOString() } : {}),
    }),
    [paginationModel, sortModel, debouncedSearch, channels, outcomes, recordedFrom, recordedTo],
  );

  const query = useQuery({
    queryKey: queryKeys.communicationLog.list(selectedTenantGid, filter),
    queryFn: ({ signal }) => listCommunicationLog(filter, signal),
    placeholderData: keepPreviousData,
    enabled: !hasNoTenant,
  });

  const columns = useMemo<GridColDef<CommunicationLogListItem>[]>(
    () => [
      {
        field: 'recordedAt',
        headerName: t('communication.columns.recordedAt'),
        width: 165,
        valueGetter: (_value, row) => formatDateTime(row.recordedAt),
      },
      {
        field: 'channel',
        headerName: t('communication.columns.channel'),
        width: 150,
        renderCell: (params) => <CommunicationChannelChip channel={params.row.channel} />,
      },
      {
        field: 'outcome',
        headerName: t('communication.columns.outcome'),
        width: 130,
        renderCell: (params) => <CommunicationOutcomeChip outcome={params.row.outcome} />,
      },
      // Only the cross-tenant view has anything to say in this column: with a tenant selected,
      // every row is that tenant's.
      ...(crossTenant
        ? [
            {
              field: 'tenantName',
              headerName: t('communication.columns.tenant'),
              width: 140,
              valueGetter: (_value: unknown, row: CommunicationLogListItem) => row.tenantName ?? '—',
            } satisfies GridColDef<CommunicationLogListItem>,
          ]
        : []),
      {
        field: 'messageId',
        headerName: t('communication.columns.messageId'),
        width: 190,
        valueGetter: (_value, row) => row.messageId ?? '—',
      },
      {
        field: 'externalDocumentId',
        headerName: t('communication.columns.externalDocumentId'),
        width: 170,
        sortable: false,
        valueGetter: (_value, row) => row.externalDocumentId ?? '—',
      },
      {
        field: 'reasonCode',
        headerName: t('communication.columns.reason'),
        width: 180,
        valueGetter: (_value, row) => row.reasonCode ?? '—',
      },
      {
        field: 'deliveryCount',
        headerName: t('communication.columns.delivery'),
        width: 90,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_value, row) => row.deliveryCount ?? '',
      },
      {
        field: 'message',
        headerName: t('communication.columns.message'),
        flex: 1,
        minWidth: 260,
        sortable: false,
      },
    ],
    [crossTenant, t],
  );

  const noRowsOverlay = useCallback(
    () => <GridEmptyOverlay message={hasNoTenant ? t(noTenantKey) : t('communication.empty')} />,
    [hasNoTenant, noTenantKey, t],
  );

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">{t('communication.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('communication.description')}
        </Typography>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
          <TextField
            size="small"
            label={t('communication.filters.search')}
            placeholder={t('communication.filters.searchPlaceholder')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              resetPage();
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ minWidth: 300, flexGrow: 1 }}
          />

          <Select
            size="small"
            multiple
            displayEmpty
            value={channels}
            onChange={(event) => {
              const value = event.target.value;
              setChannels(typeof value === 'string' || (value as string[]).includes('') ? [] : value);
              resetPage();
            }}
            renderValue={(selected) =>
              selected.length === 0
                ? t('communication.filters.anyChannel')
                : selected.map((channel) => t(communicationChannelLabelKeys[channel])).join(', ')
            }
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">
              <Checkbox checked={channels.length === 0} size="small" />
              <ListItemText primary={t('communication.filters.anyChannel')} />
            </MenuItem>
            {communicationChannels.map((channel) => (
              <MenuItem key={channel} value={channel}>
                <Checkbox checked={channels.includes(channel)} size="small" />
                <ListItemText primary={t(communicationChannelLabelKeys[channel])} />
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            multiple
            displayEmpty
            value={outcomes}
            onChange={(event) => {
              const value = event.target.value;
              setOutcomes(typeof value === 'string' || (value as string[]).includes('') ? [] : value);
              resetPage();
            }}
            renderValue={(selected) =>
              selected.length === 0
                ? t('communication.filters.anyOutcome')
                : selected.map((outcome) => t(communicationOutcomeLabelKeys[outcome])).join(', ')
            }
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">
              <Checkbox checked={outcomes.length === 0} size="small" />
              <ListItemText primary={t('communication.filters.anyOutcome')} />
            </MenuItem>
            {communicationOutcomes.map((outcome) => (
              <MenuItem key={outcome} value={outcome}>
                <Checkbox checked={outcomes.includes(outcome)} size="small" />
                <ListItemText primary={t(communicationOutcomeLabelKeys[outcome])} />
              </MenuItem>
            ))}
          </Select>

          <DatePicker
            label={t('communication.filters.recordedFrom')}
            value={recordedFrom}
            onChange={(value) => {
              setRecordedFrom(value);
              resetPage();
            }}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
          <DatePicker
            label={t('communication.filters.recordedTo')}
            value={recordedTo}
            onChange={(value) => {
              setRecordedTo(value);
              resetPage();
            }}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
        </Stack>
      </Paper>

      {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

      <Paper sx={{ height: 620 }}>
        <DataGrid<CommunicationLogListItem>
          rows={hasNoTenant ? [] : (query.data?.items ?? [])}
          columns={columns}
          getRowId={(row) => row.entryGid}
          loading={query.isFetching}
          rowCount={hasNoTenant ? 0 : (query.data?.pagedMetadata.totalCount ?? 0)}
          paginationMode="server"
          sortingMode="server"
          disableColumnFilter
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            resetPage();
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{ noRowsOverlay }}
          onRowClick={(params) => void navigate(`/communication/${params.row.entryGid}`)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Paper>
    </Stack>
  );
}
